<template>
  <el-dialog
    :title="mode === 'login' ? '用户登录' : '用户注册'"
    :visible.sync="innerVisible"
    width="460px"
    :close-on-click-modal="false"
    append-to-body
    @open="handleOpen"
  >
    <el-tabs v-model="mode" stretch @tab-click="resetForm">
      <el-tab-pane label="登录" name="login" />
      <el-tab-pane label="注册" name="register" />
    </el-tabs>
    <el-form label-position="top" @submit.native.prevent>
      <el-form-item label="用户名">
        <el-input ref="usernameInput" v-model.trim="form.username" maxlength="20" autocomplete="username" />
      </el-form-item>
      <el-form-item v-if="mode === 'register'" label="昵称（可选）">
        <el-input v-model.trim="form.nickname" maxlength="50" />
      </el-form-item>
      <el-form-item label="密码">
        <el-input
          v-model="form.password"
          type="password"
          maxlength="64"
          show-password
          :autocomplete="mode === 'login' ? 'current-password' : 'new-password'"
        />
        <div v-if="mode === 'register'" class="form-tip">密码长度为 8 至 64 位</div>
      </el-form-item>
      <el-form-item v-if="mode === 'register'" label="选择头像">
        <div class="avatar-grid">
          <button
            v-for="avatar in avatars"
            :key="avatar.filename"
            type="button"
            class="avatar-option"
            :class="{ selected: form.avatarFilename === avatar.filename }"
            :title="avatar.label"
            @click="form.avatarFilename = avatar.filename"
          >
            <el-avatar :size="48" :src="avatarUrl(avatar.filename)"><i class="el-icon-user-solid" /></el-avatar>
          </button>
        </div>
      </el-form-item>
      <el-form-item label="验证码">
        <div class="captcha-row">
          <el-input v-model.trim="form.captchaCode" maxlength="3" placeholder="请输入3位数字" @keyup.enter.native="handleSubmit" />
          <button type="button" class="captcha-image" title="点击刷新验证码" @click="loadCaptcha">
            <img v-if="captchaImage" :src="captchaImage" alt="图形验证码">
            <span v-else>点击刷新</span>
          </button>
        </div>
      </el-form-item>
      <el-alert v-if="error" type="error" :title="error" :closable="false" show-icon />
    </el-form>
    <span slot="footer">
      <el-button :disabled="loading" @click="innerVisible = false">取消</el-button>
      <el-button type="primary" :loading="loading" @click="handleSubmit">
        {{ mode === 'login' ? '登录' : '注册并登录' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { getCaptcha, login, register } from '../api/auth'
import { authBus } from '../store/auth'
import { AVATARS, DEFAULT_AVATAR, avatarUrl } from '../config/avatars'

export default {
  name: 'LoginDialog',
  props: { visible: { type: Boolean, default: false } },
  data() {
    return {
      mode: 'login',
      form: { username: '', password: '', nickname: '', avatarFilename: DEFAULT_AVATAR, captchaCode: '' },
      captchaId: '', captchaImage: '', error: '', loading: false, avatars: AVATARS
    }
  },
  computed: {
    innerVisible: {
      get() { return this.visible },
      set(value) { this.$emit('update:visible', value) }
    }
  },
  methods: {
    avatarUrl,
    handleOpen() {
      this.resetForm()
      this.$nextTick(() => this.$refs.usernameInput && this.$refs.usernameInput.focus())
    },
    resetForm() {
      this.form.password = ''
      this.form.nickname = ''
      this.form.captchaCode = ''
      this.error = ''
      this.loadCaptcha()
    },
    async loadCaptcha() {
      this.form.captchaCode = ''
      try {
        const { data } = await getCaptcha()
        if (data.code !== 200) throw new Error(data.message || '验证码加载失败')
        this.captchaId = data.data.captchaId
        this.captchaImage = data.data.captchaImage
      } catch (error) {
        this.captchaId = ''
        this.captchaImage = ''
        this.error = this.resolveError(error, '验证码加载失败')
      }
    },
    async handleSubmit() {
      if (!this.form.username || !this.form.password || !/^\d{3}$/.test(this.form.captchaCode)) {
        this.error = '请完整填写用户名、密码和3位数字验证码'
        return
      }
      if (this.mode === 'register' && this.form.password.length < 8) {
        this.error = '密码长度不能少于8位'
        return
      }
      this.loading = true
      this.error = ''
      try {
        const payload = { ...this.form, captchaId: this.captchaId }
        const { data } = this.mode === 'login' ? await login(payload) : await register(payload)
        if (data.code !== 200) throw new Error(data.message || '操作失败')
        authBus.login(data.data)
        this.innerVisible = false
        this.$message.success(this.mode === 'login' ? '登录成功' : '注册成功')
        this.$emit('success', data.data.user)
      } catch (error) {
        this.error = this.resolveError(error, this.mode === 'login' ? '登录失败' : '注册失败')
      } finally {
        this.loading = false
        await this.loadCaptcha()
      }
    },
    resolveError(error, fallback) {
      return error.response && error.response.data && error.response.data.message
        ? error.response.data.message : error.message || fallback
    }
  }
}
</script>

<style scoped>
.form-tip { color: var(--ink-3); font-size: 12px; }
.captcha-row { display: grid; grid-template-columns: 1fr 130px; gap: 12px; }
.captcha-image { height: 42px; padding: 0; overflow: hidden; cursor: pointer; background: var(--surface-2); border: 1px solid var(--line); border-radius: var(--r-md); }
.captcha-image img { width: 100%; height: 100%; object-fit: cover; }
.avatar-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.avatar-option { padding: 7px; cursor: pointer; background: var(--surface); border: 2px solid transparent; border-radius: var(--r-md); }
.avatar-option.selected { border-color: var(--brand); background: var(--brand-soft); }
</style>
